import React, { useState, useEffect, useMemo, memo } from "react";
import { useParams } from "react-router-dom";

import LtiLoadingSpinner from "../../components/Lti/LtiLoadingSpinner";
import LtiPlayer from "../../components/Lti/LtiPlayer";
import PageError from "../../components/Error/PageError";
import { EmbeddedActivityPreview } from "../../components/EmbeddedActivityPreview/EmbeddedActivityPreview";
import { apiClient } from "../../utils/apiClient";
import { useActivity } from "../../hooks/useActivity";
import { useChannel } from "../../hooks/Channel/useChannel";
import { useIsLTIUser } from "../../hooks/User/useIsLTIUser";
import { useLtiUrlParameters } from "../../hooks/LTI/useLtiUrlParameters";

const LTI_AUTOPLAY_DEFAULT = true; // Default to autostart unless the request specifies otherwise

const LtiActivity = () => {
  const { channelId, activityId } = useParams();

  const [pageError, setPageError] = useState(false);
  const [pageErrorTitle, setPageErrorTitle] = useState("");
  const [pageErrorMessage, setPageErrorMessage] = useState("");
  const [activityHasPlayed, setActivityHasPlayed] = useState(false);
  const isLtiUser = useIsLTIUser();
  const {
    channel,
    error: channelError,
    isFetchingChannel,
  } = useChannel(channelId);
  const { activity, error: activityError, isFetchingActivity } = useActivity(activityId);
  const {
    lisResultSourcedId = "",
    lisOutcomeServiceUrl = "",
    autoplay,
    // zspace,
  } = useLtiUrlParameters() || {};

  useEffect(() => {
    if (isLtiUser === false) {
      setPageErrorTitle("Permission error");
      setPageErrorMessage("You do not have permission to view this activity.");
      setPageError(true);
      console.error(
        "[LtiActivity] User is not an LTI user."
      );
    } else if (activityError || channelError) {
      // setPageErrorTitle("Error");
      setPageErrorMessage("The requested activity could not be found.");
      setPageError(true);
    }
  }, [isLtiUser, activityError, channelError]);

  const shouldAutoplay = useMemo(() => {
    if (autoplay === false) {
      return false;
    } else {
      return autoplay || LTI_AUTOPLAY_DEFAULT;
    }
  }, [autoplay]);

  const isCurrentlyLoading = useMemo(() => {
    // Check if we're still determining LTI user status
    if (isLtiUser === undefined || isLtiUser === null) {
      return true;
    }
    // Check if LTI params have loaded, but only if LTI user
    if (isLtiUser && autoplay === null) {
      return true;
    }

    // Check if the channel/activity data is still loading
    if ((isFetchingChannel || isFetchingActivity) && !channelError && !activityError) {
      return true;
    }

    return false;
  }, [isLtiUser, autoplay, isFetchingChannel, isFetchingActivity, channelError, activityError]);

  const readyToRenderPlayer = activity && channel && isLtiUser;
  const shouldRenderActivity = shouldAutoplay || activityHasPlayed;
  const supportsLtiBasicOutcome = Boolean(lisResultSourcedId && lisOutcomeServiceUrl);

  const postActivityScore = async (score) => {
      try {
        const response = await apiClient({
          method: "POST",
          path: "lti/gradepassback",
          data: {
            lis_result_sourcedid: lisResultSourcedId,
            lis_outcome_service_url: lisOutcomeServiceUrl,
            grade: score,
          },
        });

        return Promise.resolve(response);
      } catch (error) {
        console.error("[LtiActivity - postActivityScore] Error posting activity score:", error);
        return Promise.reject(error);
      }
  };

  const fetchPreviousActivityScore = async () => {
    return Promise.resolve(undefined); // Assuming no value until the API endpoint is implemented
  };

  const onPlayerCreated = (player) => {
    const activityOptions = {
      title: activity.title ? activity.title : "",
      canEdit: false,
    };

    player.LoadActivityFromJSONString(activity.data, activityOptions);
  };

  // Rendering logic
  if (isCurrentlyLoading) {
    return <LtiLoadingSpinner />;
  }

  if (pageError) {
    return (
      <PageError
        error={pageError}
        title={pageErrorTitle}
        errorMessage={pageErrorMessage}
      />
    );
  }

  if (readyToRenderPlayer && shouldRenderActivity) {
    return (
      <LtiPlayer
        activity={activity}
        channelId={channelId}
        activityId={activityId}
        onPlayerCreated={onPlayerCreated}
        supportsLtiBasicOutcome={supportsLtiBasicOutcome}
        postActivityScore={postActivityScore}
        fetchPreviousActivityScore={fetchPreviousActivityScore}
      />
    );
  }

  if (readyToRenderPlayer && !shouldRenderActivity) {
    return (
      <EmbeddedActivityPreview
        title={activity.title}
        image={activity.image}
        description={activity.description}
        channelName={activity.channel_name}
        channelImage={channel?.image_url}
        onLoadActivity={() => setActivityHasPlayed(true)}
      />
    );
  }

  return <LtiLoadingSpinner />;
};

LtiActivity.displayName = "LtiActivity";
export default memo(LtiActivity);
